import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LogIn, Mail, Lock, ShieldCheck, Eye, EyeOff, KeyRound, X } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { useAuth, ADMIN_ACCOUNTS } from "@/lib/auth";
import { useTheme, type LanguageCode } from "@/lib/theme-context";
import { detectHeadLang, pickT } from "@/lib/i18n";
import { useUserAuth } from "@/lib/user-auth";
import { getFirebaseAuth } from "@/lib/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";

export const Route = createFileRoute("/login")({
  head: ({ match }) => {
    const lang = detectHeadLang(match.search as Record<string, unknown>);
    return {
      meta: [
        { title: `${pickT(lang, "تسجيل الدخول", "Sign in", "Connexion")} — Rawan UI` },
        { name: "robots", content: "noindex, nofollow" },
      ],
    };
  },
  component: LoginPage,
});

function LoginPage() {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr?: string) => pickT(language, ar, en, fr);
  const { isAuthed, login } = useAuth();          // admin
  const { user, signIn } = useUserAuth();         // public user
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    if (isAuthed) navigate({ to: "/dashboard" });
    else if (user) navigate({ to: "/account" });
  }, [isAuthed, user, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setError(null);
    const normalized = email.trim().toLowerCase();
    const isAdminEmail = ADMIN_ACCOUNTS.some((a) => a.email === normalized);
    if (isAdminEmail) {
      const res = await login(email, password);
      if (!res.ok) {
        setLoading(false);
        setError(localizeAuthMessage(res.error, language));
        return;
      }
      try {
        const fbAuth = getFirebaseAuth();
        if (fbAuth) {
          try {
            await signInWithEmailAndPassword(fbAuth, normalized, password);
          } catch (err) {
            const code = (err as { code?: string }).code ?? "";
            if (code === "auth/user-not-found" || code === "auth/invalid-credential") {
              try {
                await createUserWithEmailAndPassword(fbAuth, normalized, password);
              } catch { /* ignore */ }
            }
          }
        }
      } catch { /* ignore */ }
      setLoading(false);
      navigate({ to: "/dashboard" });
      return;
    }
    const res = await signIn(email, password);
    setLoading(false);
    if (!res.ok) setError(localizeAuthMessage(res.error, language));
    else navigate({ to: "/account" });
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12 max-w-md grid place-items-center">
        <form onSubmit={onSubmit} className="w-full bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-card animate-scale-in">
          <div className="grid place-items-center w-14 h-14 mx-auto rounded-2xl mb-4" style={{ background: "var(--gradient-primary)" }}>
            <ShieldCheck className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-extrabold text-center">{t("تسجيل الدخول", "Sign in", "Connexion")}</h1>
          <p className="text-sm text-muted-foreground text-center mt-1">
            {t("مرحباً بعودتك", "Welcome back", "Bon retour")}
          </p>

          <label className="block mt-6 text-sm font-bold">{t("البريد الإلكتروني", "Email address", "Adresse e-mail")}</label>
          <div className="relative mt-1.5">
            <Mail className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-muted-foreground" />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="w-full ps-9 pe-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>

          <div className="flex items-center justify-between mt-4">
            <label className="block text-sm font-bold">{t("كلمة المرور", "Password", "Mot de passe")}</label>
            <button
              type="button"
              onClick={() => setResetOpen(true)}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              <KeyRound className="h-3 w-3" />
              {t("نسيت كلمة المرور؟", "Forgot password?", "Mot de passe oublié ?")}
            </button>
          </div>
          <div className="relative mt-1.5">
            <Lock className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-muted-foreground" />
            <input
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              className="w-full ps-9 pe-10 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t("إخفاء كلمة المرور", "Hide password", "Masquer le mot de passe") : t("إظهار كلمة المرور", "Show password", "Afficher le mot de passe")}
              className="absolute top-1/2 -translate-y-1/2 end-2 p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {error && <div className="mt-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg p-3">{error}</div>}

          <button type="submit" disabled={loading} className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <LogIn className="h-4 w-4" /> {loading ? t("جارٍ التحقق…", "Checking…", "Vérification…") : t("دخول", "Sign in", "Connexion")}
          </button>

          <div className="mt-4 text-center text-xs text-muted-foreground">
            {t("ليس لديك حساب؟", "No account?", "Pas de compte ?")} <Link to="/signup" className="text-primary hover:underline">{t("أنشئ حساباً", "Create one", "Créer un compte")}</Link>
          </div>

          <div className="mt-3 text-center text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">{t("عودة للرئيسية", "Back home", "Retour à l’accueil")}</Link>
          </div>
        </form>
      </main>
      {resetOpen && <ResetPasswordModal onClose={() => setResetOpen(false)} initialEmail={email} />}
      <Footer />
    </div>
  );
}

function ResetPasswordModal({ onClose, initialEmail }: { onClose: () => void; initialEmail: string }) {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr?: string) => pickT(language, ar, en, fr);
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setMsg(null);
    try {
      const auth = getFirebaseAuth();
      if (!auth) throw new Error("Firebase غير متاح");
      await sendPasswordResetEmail(auth, email.trim().toLowerCase(), {
        url: window.location.origin + "/login",
      });
      setMsg({ type: "ok", text: t("تم إرسال رابط إعادة التعيين إلى بريدك الإلكتروني. تحقّق من البريد الوارد (وأحياناً الـ Spam).", "Password reset link sent. Check your inbox (and sometimes Spam).", "Lien de réinitialisation envoyé. Vérifiez votre boîte de réception (et parfois les spams).") });
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      const map: Record<string, string> = {
        "auth/invalid-email": t("بريد إلكتروني غير صالح", "Invalid email address", "Adresse e-mail invalide"),
        "auth/user-not-found": t("لا يوجد حساب بهذا البريد", "No account uses this email", "Aucun compte avec cet e-mail"),
        "auth/missing-email": t("أدخل بريدك الإلكتروني أولاً", "Enter your email first", "Saisissez d’abord votre e-mail"),
        "auth/too-many-requests": t("محاولات كثيرة، حاول لاحقاً", "Too many attempts, try later", "Trop de tentatives, réessayez plus tard"),
      };
      setMsg({ type: "err", text: map[code] ?? t("تعذّر إرسال البريد، حاول مجدداً.", "Could not send the email, please try again.", "Impossible d’envoyer l’e-mail, réessayez.") });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-sm bg-card border border-border rounded-2xl p-6 shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-extrabold flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            {t("استعادة كلمة المرور", "Reset password", "Réinitialiser le mot de passe")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("إغلاق", "Close", "Fermer")}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          {t("أدخل بريدك الإلكتروني وسنرسل لك رابطاً لإعادة ضبط كلمة المرور.", "Enter your email and we’ll send a password reset link.", "Saisissez votre e-mail et nous vous enverrons un lien de réinitialisation.")}
        </p>
        <form onSubmit={submit}>
          <div className="relative">
            <Mail className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-muted-foreground" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="w-full ps-9 pe-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          {msg && (
            <div
              className={`mt-3 text-xs rounded-lg border p-2.5 ${
                msg.type === "ok"
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : "border-destructive/40 bg-destructive/10 text-destructive"
              }`}
            >
              {msg.text}
            </div>
          )}
          <div className="flex items-center gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-muted"
            >
              {t("إلغاء", "Cancel", "Annuler")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary inline-flex items-center justify-center gap-2 py-2 text-sm disabled:opacity-60"
            >
              {loading ? t("جارٍ الإرسال…", "Sending…", "Envoi…") : t("إرسال الرابط", "Send link", "Envoyer le lien")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function localizeAuthMessage(message: string, lang: LanguageCode): string {
  const map: Record<string, [string, string, string]> = {
    "بيانات الدخول غير صحيحة": ["بيانات الدخول غير صحيحة", "Invalid email or password", "E-mail ou mot de passe incorrect"],
    "Firebase غير متاح": ["Firebase غير متاح", "Firebase is unavailable", "Firebase est indisponible"],
    "هذا البريد مسجل مسبقاً": ["هذا البريد مسجل مسبقاً", "This email is already registered", "Cet e-mail est déjà inscrit"],
    "بريد إلكتروني غير صالح": ["بريد إلكتروني غير صالح", "Invalid email address", "Adresse e-mail invalide"],
    "كلمة المرور ضعيفة (6 خانات على الأقل)": ["كلمة المرور ضعيفة (6 خانات على الأقل)", "Weak password (at least 6 characters)", "Mot de passe faible (au moins 6 caractères)"],
    "محاولات كثيرة، حاول لاحقاً": ["محاولات كثيرة، حاول لاحقاً", "Too many attempts, try later", "Trop de tentatives, réessayez plus tard"],
    "تعذّر الاتصال بالشبكة": ["تعذّر الاتصال بالشبكة", "Network connection failed", "Échec de connexion au réseau"],
    "يجب تأكيد البريد الإلكتروني أولاً. تم إرسال رسالة جديدة إلى بريدك.": ["يجب تأكيد البريد الإلكتروني أولاً. تم إرسال رسالة جديدة إلى بريدك.", "Please verify your email first. A new message was sent.", "Veuillez d’abord confirmer votre e-mail. Un nouveau message a été envoyé."],
    "تم حظر هذا الحساب من قِبَل الإدارة": ["تم حظر هذا الحساب من قِبَل الإدارة", "This account has been banned by admin", "Ce compte a été bloqué par l’administration"],
  };
  const hit = map[message];
  return hit ? pickT(lang, hit[0], hit[1], hit[2]) : message;
}
